export const ACCESS_TOKEN_KEY = "dgplus_access_token"
export const REFRESH_TOKEN_KEY = "dgplus_refresh_token"
export const TOKEN_EXPIRES_ON = "dgplus_token_expires_on"

export type Token = string | undefined

/**
 * We use cookies and localStorage to persist the `accessToken`, and `refreshToken`
 * in the browser respectively so they can be used by the client to authenticate
 * API requests. Since the cookie is not HTTP-only, it can be used by not only our
 * own client, but also any other external scripts that are loaded on the same
 * domain. The same goes for localStorage. This is a known security vulnerability,
 * but we accept it for now.
 *
 * @todo [>=1.0.0] Create our own endpoint that safely stores the tokens in a
 * HTTP-only cookie that’s encoded with a server-side secret.
 */

/**
 * Retrieve the access token from the `ACCESS_TOKEN_KEY` cookie
 * @returns The token or undefined if it doesn't exist
 */
export function getAccessToken(): Token {
  return window.localStorage.getItem(ACCESS_TOKEN_KEY) as Token
}

/**
 * Store the access token in the `ACCESS_TOKEN_KEY` cookie
 * @param token The token to store
 */
export function saveAccessToken(token: string): void {
  /**
   * Expiry date of 1 year from now.
   * @todo Either use the `expires_in` value returned by the OAuth server or
   *       parse the `expires_in` value from the token.
   */
  window.localStorage.setItem(ACCESS_TOKEN_KEY, token)
}

/**
 * Destroy the `ACCESS_TOKEN_KEY` cookie
 */
export function destroyAccessToken(): void {
  window.localStorage.removeItem(ACCESS_TOKEN_KEY)
}

/**
 * Retrieve the refresh token from the `REFRESH_TOKEN_KEY` localStorage key
 * @returns The token or undefined if it doesn't exist
 */
export function getRefreshToken(): Token {
  if (typeof window === "undefined") return
  return window.localStorage.getItem(REFRESH_TOKEN_KEY) as Token
}

/**
 * Store the refresh token in the `REFRESH_TOKEN_KEY` localStorage key
 * @param token The token to store
 */
export function saveRefreshToken(token: string): void {
  if (typeof window === "undefined") return
  window.localStorage.setItem(REFRESH_TOKEN_KEY, token)
}

export function getExpiresOn(): Date | undefined {
  if (typeof window === "undefined") return
  const dateString = window.localStorage.getItem(TOKEN_EXPIRES_ON)
  if (!dateString) return
  return new Date(dateString)
}

export function saveExpiresOn(expiresOn: string): void {
  if (typeof window === "undefined") return
  window.localStorage.setItem(TOKEN_EXPIRES_ON, expiresOn)
}

/**
 * Destroy the `REFRESH_TOKEN_KEY` localStorage key
 */
export function destroyRefreshToken(): void {
  if (typeof window === "undefined") return
  window.localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/**
 * Destroy the `expiresOn` localStorage key
 */
export function destroyExpiresOn(): void {
  if (typeof window === "undefined") return
  window.localStorage.removeItem(TOKEN_EXPIRES_ON)
}
